/* eslint-disable sonarjs/no-duplicate-string */
import debounce from 'debounce';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import Focus from '@tiptap/extension-focus';
import Text from '@tiptap/extension-text';
import { ProsemirrorHelper, InputDocNode, MergeValueNode, ParagraphNode, EditorHelper, } from '@getaccept/editor-lib-new';
import { onBeforeUnmount, onMounted, computed, defineComponent, ref, watch } from 'vue';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import Placeholder from '@tiptap/extension-placeholder';
import { validateEmail } from '@getaccept/lib-shared-new/src/helpers';
import { IconSize } from '@getaccept/fried-tofu';
export default defineComponent({
    components: { EditorContent },
    props: {
        inputField: { type: Object, required: true },
        recipientInput: { type: Object, default: null },
        fieldFocused: { type: Boolean },
        editable: { type: Boolean },
        isUpdating: { type: Boolean },
        isRequired: { type: Boolean },
        mergeValues: { type: Object, required: true },
        fields: { type: Array, default: () => [] },
        hasUpdateError: { type: Boolean },
    },
    emits: ['update', 'validate-form-input'],
    setup(props, { emit }) {
        const editor = ref(null);
        const emailErrorMessage = ref('');
        const hasError = computed(() => props.hasUpdateError || !!emailErrorMessage.value);
        const errorMessage = computed(() => emailErrorMessage.value || '');
        const plainContent = computed(() => editor.value ? ProsemirrorHelper.getInnerTextContent(editor.value.state).trim() : '');
        const updateValidationData = () => {
            if (plainContent.value === '') {
                emit('update', plainContent.value);
                emit('validate-form-input', !props.isRequired);
                emailErrorMessage.value = props.isRequired
                    ? 'input_fields_error_field_not_correctly_filled_out'
                    : '';
                return;
            }
            const isValid = validateEmail(plainContent.value);
            emit('validate-form-input', isValid);
            emailErrorMessage.value = isValid ? '' : 'input_fields_error_field_not_correctly_filled_out';
            if (isValid) {
                emit('update', plainContent.value);
            }
        };
        const update = () => {
            if (!props.editable) {
                return;
            }
            updateValidationData();
        };
        const debounceUpdate = debounce(update, 300);
        watch(() => props.fieldFocused, () => {
            if (props.fieldFocused) {
                editor.value.view.focus();
            }
        });
        watch(() => props.recipientInput, () => {
            if (!props.recipientInput) {
                return;
            }
            const content = ProsemirrorHelper.getInputContent(props.recipientInput.value, props.fields);
            editor.value.commands.setContent(content);
        });
        watch(() => props.editable, () => {
            editor.value.setEditable(props.editable);
        });
        const initialContent = () => {
            var _a;
            const inputField = EditorHelper.hasMergeTags(props.inputField.value)
                ? ProsemirrorHelper.getMergeValueFromMergeTag(ProsemirrorHelper.getTagIdFromMergeTag(props.inputField.value), props.fields, props.mergeValues)
                : props.inputField.value;
            return ProsemirrorHelper.getInputContent(((_a = props.recipientInput) === null || _a === void 0 ? void 0 : _a.value) || inputField, props.fields);
        };
        const editorConfig = computed(() => ({
            content: initialContent(),
            editable: props.editable,
            enableInputRules: false,
            enablePasteRules: false,
            extensions: [
                InputDocNode,
                Focus,
                ParagraphNode,
                Text,
                MergeValueNode,
                Placeholder.configure({
                    placeholder: t('input_fields_add_email'),
                }),
            ],
            onBlur: update,
            onUpdate: debounceUpdate,
        }));
        onMounted(() => {
            editor.value = new TipTapEditor(editorConfig.value);
        });
        onBeforeUnmount(() => {
            editor.value.destroy();
        });
        return {
            IconSize,
            editor,
            errorMessage,
            emailErrorMessage,
            hasError,
            update,
            t,
        };
    },
});
